import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import Loader from '@/components/customercenter-ui/common/loader/loader';
import { CART_STATE_FROZEN, PAYLOAD_QUERY } from '@/components/customercenter-ui/components/customer-detail/constants';
import {
  cartHasMultipleInsuranceItems,
  getInsuranceItemId,
} from '@/components/customercenter-ui/components/utils/cartUtils';
import Icon from '@/components/customercenter-ui/icons/lock';
import { Button } from '@/components/customercenter-ui/ui/button';
import { SUCCESS_CODE_OK, ORDER_TYPES } from '@/constants/constant';
import { CreditScenarios, PaymentMethods, useCheckout } from '@/context/checkoutProvider';
import { useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { routerUrl } from '@/helpers/utils/routerUrl';
import { getAgentName, getSelectedSite } from '@/helpers/utils/siteUtils';

const PlaceOrderButton = ({ setEmailError, cartHasInsurance }: any) => {
  const router = useRouter();
  const projectSettings: ProjectSettings = useProjSettings();
  const {
    contactInformation,
    tokenizeData,
    checkoutExtraDetails,
    shippingMethod,
    setOrderData,
    selectedPaymentMethod,
    formData,
    soaCustomer,
    setError,
    setNotificationMsg,
    cardDetails,
    validatePaymentBatchResult,
    contextCart,
    setContextCart,
    setCreditDecision,
  }: any = useCheckout();
  const [isLoading, setIsLoading] = useState(false);
  const { orderCart, submitSoaOrder, handleShippingInsurance } = useCart(false);
  const params = useSearchParams();
  const cartData = contextCart;
  const isNoChargeOrder = cartData?.additionalDetails?.orderType === ORDER_TYPES?.NO_CHARGE;

  const generatePayementMethod = (paymentMethod: any) => {
    if (paymentMethod === 'CARD') {
      return 'CreditCard';
    } else if (paymentMethod === 'CREDIT') {
      return 'MasonCredit';
    } else if (paymentMethod === 'CASH') {
      return 'Cash';
    } else if (paymentMethod === 'CHECK') {
      return 'Check';
    } else if (paymentMethod === 'PAYPAL') {
      return 'PayPal';
    } else if (paymentMethod === PaymentMethods.NO_CHARGE) {
      return 'NoCharge';
    }
  };

  const placeOrder = async () => {
    if (!isNoChargeOrder) {
      if (cartHasInsurance) {
        if (!contactInformation?.email) {
          return;
        } else {
          if (contactInformation?.email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(contactInformation?.email)) {
              return;
            }
            formData.email = contactInformation?.email;
          }
        }
      } else if (!contactInformation?.email && !checkoutExtraDetails?.sendPromotionalEmails) {
        setEmailError('Please input email address or check the No  Email Address provided');
        return;
      } else if (contactInformation?.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(contactInformation?.email)) {
          return;
        }
        formData.email = contactInformation?.email;
      }
    }
    setIsLoading(true);
    checkoutExtraDetails['shippingMethod'] = shippingMethod;
    checkoutExtraDetails['selectedPaymentMethod'] = generatePayementMethod(selectedPaymentMethod);
    checkoutExtraDetails['soaCustomerDetails'] = soaCustomer
      ? {
          Customer: soaCustomer,
        }
      : null;
    checkoutExtraDetails['formData'] = formData ? formData : null;
    checkoutExtraDetails['validatePaymentBatchResult'] = validatePaymentBatchResult ? validatePaymentBatchResult : null;
    checkoutExtraDetails['agentName'] = getAgentName().replace(/@.*/, '');

    const divisionCode = params.get(PAYLOAD_QUERY.DIVISION_CODE) ?? 'ZB';

    try {
      const insuranceLineItemId = getInsuranceItemId(cartData);
      if (!projectSettings?.isNarvarEnabled && insuranceLineItemId) {
        const responseCart = await handleShippingInsurance({ action: 'remove', lineItemId: insuranceLineItemId });
        if (responseCart?.cartId) {
          setContextCart(responseCart);
        }
      }
      if (projectSettings?.isNarvarEnabled && cartHasMultipleInsuranceItems(cartData)) {
        const responseCart = await handleShippingInsurance({ action: 'validate' });
        if (responseCart?.cartId) {
          setContextCart(responseCart);
        }
      }
      const order = await orderCart(cardDetails, contactInformation, tokenizeData, checkoutExtraDetails, divisionCode);
      setIsLoading(false);
      if (order?.orderId) {
        setOrderData(order);
        setIsLoading(true);
        let queryParam;

        try {
          const soaSumitOrderResponse = await submitSoaOrder(order?.orderId);
          if (
            soaSumitOrderResponse !== undefined &&
            soaSumitOrderResponse.hasOwnProperty('code') &&
            soaSumitOrderResponse?.code?.toUpperCase() === SUCCESS_CODE_OK
          ) {
            const creditDecision = soaSumitOrderResponse?.CreditStatus?.creditDecision ?? '';
            setCreditDecision(creditDecision);
            if (creditDecision) {
              switch (creditDecision) {
                case CreditScenarios.DownPaymentNeeded:
                  queryParam = '?rtca_modal=on';
                  break;
                case CreditScenarios.Approved:
                  queryParam = '?rtca_order=approved';
                  break;
                case CreditScenarios.Pending:
                  queryParam = '?rtca_order=pending';
                  break;
                default:
                  break;
              }
            }
          }
        } catch (error: any) {
          console.error('There was an error while submitting order to SOA.');
        }

        setIsLoading(false);
        let confirmationPageURL = routerUrl.thankYou;
        if (queryParam) {
          confirmationPageURL += queryParam;
        }
        router.push(confirmationPageURL);
      }
    } catch (error: any) {
      const errorOutOfStock = error?.message?.replace(/\s+/g, '')?.toLowerCase()?.includes('outofstock');
      const errorDiscountCode = cartData?.discountCodes?.some(
        (discount: any) =>
          discount?.discountId && error?.message?.toLowerCase()?.includes(discount?.discountId?.toLowerCase()),
      );
      setError(true);
      setIsLoading(false);
      if (errorOutOfStock) {
        setNotificationMsg(mapLocaleToMeaningfulFormat([getSelectedSite() ?? 'ZBSite']).cartItemOutOfStockError);
        router.push(routerUrl.cart);
      } else if (errorDiscountCode) {
        setNotificationMsg(mapLocaleToMeaningfulFormat()?.invalidPromoCodeError);
      } else {
        setNotificationMsg(error?.message);
      }
      window.scrollTo(0, 0);
    }
  };

  const [isCartHold, setIscartHold] = useState(false);
  useEffect(() => {
    if (cartData?.cartState === CART_STATE_FROZEN) {
      setIscartHold(true);
    } else {
      setIscartHold(false);
    }
  }, [cartData]);

  return (
    <Button
      disabled={isCartHold}
      type="submit"
      className={`${
        !isLoading ? 'bg-deepBlue text-white' : 'bg-gray-400'
      } py-4 text-[22px] font-bold leading-7  disabled:bg-gray-400 lg:py-3 lg:text-lg lg:leading-6`}
      onClick={placeOrder}
    >
      <section className="flex items-center gap-2">
        <Icon className="h-6 w-6 fill-[#FFFFFF]" />
        {isLoading && <Loader />}
        <p className="text-xl font-bold leading-7 text-white">{mapLocaleToMeaningfulFormat().placeOrder}</p>
      </section>
    </Button>
  );
};

export default PlaceOrderButton;
